/*
 * ecom-search-overlay-lazy.js  (Algolia usage patch, 2026-09-30)
 *
 * Replaces the inline header search-overlay <script> (the one containing
 * "var POPULAR_SEARCHES" / "overlay-hits-default").
 *
 * What changed vs the live script:
 *   1. No InstantSearch instance and NO Algolia request at page load.
 *      InstantSearch is created lazily on first focus / click / input of the
 *      header search box (or when the overlay is opened).
 *   2. searchFunction guard: queries shorter than MIN_CHARS (2) are never sent
 *      (helper.search() is simply not called). Empty "recommended products"
 *      query on open is gone.
 *   3. 300 ms debounce on both the header input and the overlay's own
 *      search box (queryHook).
 *   4. Crawler guard: bots / headless / webdriver never initialise Algolia.
 *   5. Uses the Algolia Shopify app's search-only key when available
 *      (window.algoliaShopify.config.search_api_key), falling back to the key
 *      the live theme used.
 *   6. Loads algoliasearch / instantsearch from jsDelivr only if they are not
 *      already on the page, so the two <script src> tags in the header can be
 *      removed later (optional).
 */
(function () {
  'use strict';

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 300;

  var CONFIG = {
    appId: 'SKNBBZXEDC',
    // Fallback only. Prefer the app's search-only key (read at init time).
    // TODO after key rotation: replace or remove this fallback.
    indexName: 'shopify_products'
  };

  var LIBS = [
    { test: function () { return typeof window.algoliasearch === 'function'; },
      src: 'https://cdn.jsdelivr.net/npm/algoliasearch@4.20.0/dist/algoliasearch-lite.umd.js' },
    { test: function () { return typeof window.instantsearch === 'function'; },
      src: 'https://cdn.jsdelivr.net/npm/instantsearch.js@4.56.0/dist/instantsearch.production.min.js' }
  ];

  // ---- crawler guard -------------------------------------------------------
  var IS_BOT = (function () {
    try {
      if (navigator.webdriver) return true;
      return /bot|crawl|spider|slurp|headless|lighthouse/i.test(navigator.userAgent || '');
    } catch (e) { return false; }
  })();

  // ADJUST ME: replace with your own real popular search terms.
  var POPULAR_SEARCHES = [
    'dowel pins', 'self clinching studs', '1/4-20 x 3/4 fhcs',
    'black oxide washers', 'shoulder screws', 'socket set screw',
    'phillips flat undercut', '#12-24 screw'
  ];

  var headerInput = document.getElementById('ecom-header-search-input');
  var overlay = document.getElementById('ecom-search-overlay');
  if (!headerInput || !overlay) return;

  var backdrop = document.getElementById('ecom-search-overlay-backdrop');
  var closeBtn = document.getElementById('ecom-search-overlay-close');
  var defaultState = document.getElementById('overlay-default-state');
  var resultsState = document.getElementById('overlay-results-state');
  var popularList = document.getElementById('overlay-popular-list');
  var defaultHits = document.getElementById('overlay-hits-default');

  var mobileFiltersPanel = document.getElementById('overlay-mobile-filters');
  var mobileFiltersBackdrop = document.getElementById('overlay-mobile-filters-backdrop');
  var mobileFilterFab = document.getElementById('overlay-mobile-filter-fab');
  var mobileFiltersClose = document.getElementById('overlay-mobile-filters-close');
  var mobileFiltersClear = document.getElementById('overlay-mobile-clear');
  var mobileFiltersShow = document.getElementById('overlay-mobile-show');
  var mobileFilterCountEl = document.getElementById('overlay-mobile-filter-count');

  var search = null;
  var initPromise = null;

  function escapeHtml(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function isRealQuery(q) {
    return !!q && String(q).trim().length >= MIN_CHARS;
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src; s.async = false;
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function ensureLibs() {
    return LIBS.reduce(function (p, lib) {
      return p.then(function () { return lib.test() ? null : loadScript(lib.src); });
    }, Promise.resolve());
  }

  function getSearchKey() {
    var c = window.algoliaShopify && window.algoliaShopify.config;
    return (c && c.search_api_key) || '';   // 2026-10-03: no hard-coded fallback key; no key -> no live overlay (header form still goes to /search)
  }

  function overlayCard(hit) {
    var imgSrc = hit.image ? hit.image.replace(/\.([^.]+)$/, '_300x300.$1') : '';
    var img = hit.image ? '<img src="' + imgSrc + '" alt="' + escapeHtml(hit.title) + '" loading="lazy">' : '';
    var minPrice = (hit.variants_min_price != null) ? hit.variants_min_price : hit.price;
    var price = (minPrice != null) ? ('$' + Number(minPrice).toFixed(2)) : '';
    return '<a class="overlay-card" href="/products/' + escapeHtml(hit.handle) + '">' +
      '<div class="overlay-card__img">' + img + '</div>' +
      '<div class="overlay-card__title">' + escapeHtml(hit.title) + '</div>' +
      '<div class="overlay-card__price">' + price + '</div>' +
    '</a>';
  }

  function toggleState(query) {
    var hasQuery = isRealQuery(query);
    if (defaultState) defaultState.style.display = hasQuery ? 'none' : 'grid';
    if (resultsState) resultsState.style.display = hasQuery ? 'grid' : 'none';
  }

  function setQuery(value) {
    if (!search) return;
    search.setUiState(function (uiState) {
      var next = {};
      next[CONFIG.indexName] = Object.assign({}, uiState[CONFIG.indexName], { query: value });
      return next;
    });
  }

  // ---- lazy init -----------------------------------------------------------
  function initSearch() {
    if (IS_BOT) return Promise.resolve(null);
    if (initPromise) return initPromise;
    if (!getSearchKey()) return Promise.resolve(null);

    initPromise = ensureLibs().then(function () {
      var searchClient = window.algoliasearch(CONFIG.appId, getSearchKey());
      var qhTimer;

      search = window.instantsearch({
        indexName: CONFIG.indexName,
        searchClient: searchClient,
        routing: false,
        // Guard: never send empty / 1-character queries (incl. the initial
        // search that start() would normally fire).
        searchFunction: function (helper) {
          if (!isRealQuery(helper.state.query)) return;
          helper.search();
        }
      });

      search.addWidgets([
        window.instantsearch.widgets.configure({ hitsPerPage: 12 }),

        window.instantsearch.widgets.searchBox({
          container: '#overlay-searchbox',
          placeholder: 'Search...',
          showSubmit: false,
          showReset: true,
          // 300 ms debounce; header input kept in sync.
          queryHook: function (query, refine) {
            headerInput.value = query;
            clearTimeout(qhTimer);
            qhTimer = setTimeout(function () {
              refine(query);
              toggleState(query);
            }, DEBOUNCE_MS);
          }
        }),

        window.instantsearch.widgets.stats({
          container: '#overlay-stats',
          templates: { text: function (data) { return '<strong>' + data.nbHits.toLocaleString() + '</strong> results found'; } }
        }),
        window.instantsearch.widgets.stats({
          container: '#overlay-mobile-count',
          templates: { text: function (data) { return data.nbHits.toLocaleString() + ' results'; } }
        }),

        window.instantsearch.widgets.refinementList({ container: '#overlay-filter-category', attribute: 'product_type', limit: 5, showMore: true, showMoreLimit: 20 }),
        window.instantsearch.widgets.refinementList({ container: '#overlay-filter-material', attribute: 'meta.custom.material', limit: 5, showMore: true, showMoreLimit: 20 }),
        window.instantsearch.widgets.refinementList({ container: '#overlay-filter-drive', attribute: 'meta.custom.drive_type', limit: 5, showMore: true, showMoreLimit: 20 }),
        window.instantsearch.widgets.refinementList({ container: '#overlay-filter-finish', attribute: 'meta.custom.finish', limit: 5, showMore: true, showMoreLimit: 20 }),

        window.instantsearch.widgets.hits({
          container: '#overlay-hits',
          templates: {
            item: overlayCard,
            empty: function () { return '<div style="padding:40px;text-align:center;color:#6b7280;">No results found.</div>'; }
          }
        })
        // NOTE: the old "#overlay-hits-default" hits widget (empty-query
        // "Recommended products") was removed: it cost one search per page.
      ]);

      search.on('render', function () {
        var uiState = search.getUiState()[CONFIG.indexName] || {};
        var refinementList = uiState.refinementList || {};
        var count = 0;
        Object.keys(refinementList).forEach(function (attr) { count += (refinementList[attr] || []).length; });
        if (mobileFilterCountEl) mobileFilterCountEl.textContent = count > 0 ? ' (' + count + ')' : '';
      });

      search.start(); // no request: searchFunction blocks the empty initial query

      // If the user already typed before init finished, run that query now.
      if (isRealQuery(headerInput.value)) {
        setQuery(headerInput.value);
        toggleState(headerInput.value);
      }
      return search;
    }).catch(function (err) {
      initPromise = null; // allow retry
      if (window.console) console.warn('[ecom-search] init failed', err);
      return null;
    });
    return initPromise;
  }

  // ---- static UI (no network) ----------------------------------------------
  if (popularList) {
    POPULAR_SEARCHES.forEach(function (term) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = term;
      btn.addEventListener('click', function () {
        headerInput.value = term;
        initSearch().then(function () { setQuery(term); toggleState(term); });
      });
      li.appendChild(btn);
      popularList.appendChild(li);
    });
  }

  // Default state no longer shows query-driven "Recommended products".
  if (defaultHits && !defaultHits.innerHTML.trim()) {
    defaultHits.innerHTML = '<div style="padding:24px 8px;color:#6b7280;">Type at least ' + MIN_CHARS +
      ' characters to search, or pick a popular search.</div>';
  }

  var siteHeader = overlay ? overlay.closest('.ecom-site-header') : null;
  function openOverlay() {
    overlay.classList.add('is-open');
    if (siteHeader) siteHeader.classList.add('is-search-open'); // z-index fix, see the ecom-header.liquid CSS
    document.body.style.overflow = 'hidden';
  }
  function closeOverlay() {
    overlay.classList.remove('is-open');
    if (siteHeader) siteHeader.classList.remove('is-search-open');
    document.body.style.overflow = '';
  }
  function openMobileFilters() {
    if (mobileFiltersPanel) mobileFiltersPanel.classList.add('is-open');
    if (mobileFiltersBackdrop) mobileFiltersBackdrop.classList.add('is-open');
  }
  function closeMobileFilters() {
    if (mobileFiltersPanel) mobileFiltersPanel.classList.remove('is-open');
    if (mobileFiltersBackdrop) mobileFiltersBackdrop.classList.remove('is-open');
  }
  function clearAllRefinements() {
    if (!search) return;
    search.setUiState(function (uiState) {
      var next = {};
      var indexState = Object.assign({}, uiState[CONFIG.indexName]);
      delete indexState.refinementList;
      next[CONFIG.indexName] = indexState;
      return next;
    });
  }

  if (mobileFilterFab) mobileFilterFab.addEventListener('click', openMobileFilters);
  if (mobileFiltersClose) mobileFiltersClose.addEventListener('click', closeMobileFilters);
  if (mobileFiltersBackdrop) mobileFiltersBackdrop.addEventListener('click', closeMobileFilters);
  if (mobileFiltersShow) mobileFiltersShow.addEventListener('click', closeMobileFilters);
  if (mobileFiltersClear) mobileFiltersClear.addEventListener('click', clearAllRefinements);

  // QW 2026-10-03: exact part numbers. When the typed text looks like a SKU and Shopify's own
  // predictive search confirms one exact match (ecom-sku-jump.js), show a direct link and do NOT
  // send that query to Algolia. Enter then goes straight to the product (submit handler in
  // ecom-sku-jump.js). Anything else falls through to Algolia exactly as before.
  var skuBox = null;
  function showSkuMatch(url, q) {
    if (!skuBox) {
      skuBox = document.createElement('div');
      skuBox.className = 'overlay-sku-match';
      skuBox.style.cssText = 'margin:0 0 12px;padding:12px 14px;border:2px solid #002F6C;border-radius:8px;background:#f0f6ff;font-size:15px;';
      var panel = overlay.querySelector('.ecom-search-overlay__panel') || overlay;
      var before = document.getElementById('overlay-default-state');
      if (before && before.parentNode === panel) panel.insertBefore(skuBox, before); else panel.appendChild(skuBox);
    }
    if (!url) { skuBox.style.display = 'none'; return; }
    skuBox.innerHTML = 'Exact part number match: <a href="' + escapeHtml(url) + '" style="font-weight:700;color:#002F6C;text-decoration:underline;">' +
      escapeHtml(q) + ' &rarr; view product</a> <span style="color:#4b5563;">(press Enter)</span>';
    skuBox.style.display = '';
  }
  var debounceTimer, typedSeq = 0;
  headerInput.addEventListener('input', function () {
    var value = headerInput.value;
    var seq = ++typedSeq;
    initSearch();
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      var jump = window.EcomSkuJump;
      var check = (jump && jump.looksLikeSku(value)) ? jump.resolve(value) : Promise.resolve(null);
      check.then(function (url) {
        if (seq !== typedSeq) return;          // the visitor kept typing
        showSkuMatch(url, value.trim());
        if (url) {                             // exact part number: no Algolia query
          var ob = overlay.querySelector('#overlay-searchbox input');
          if (ob && ob.value !== value) ob.value = value;
          toggleState('');
          return;
        }
        initSearch().then(function () {
          if (!search) return;
          setQuery(value);
          toggleState(value);
        });
      });
    }, DEBOUNCE_MS);
  });

  // Lazy init on first focus / click (creates InstantSearch, sends nothing).
  headerInput.addEventListener('focus', function () { openOverlay(); initSearch(); });
  headerInput.addEventListener('click', function () { initSearch(); }, { once: true });

  if (closeBtn) closeBtn.addEventListener('click', closeOverlay);
  if (backdrop) backdrop.addEventListener('click', closeOverlay);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { closeOverlay(); closeMobileFilters(); }
  });

  toggleState('');
})();
